import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import { NavLink } from 'react-router'
import cl from 'clsx'
import { UseQueryResult } from '@tanstack/react-query'

import {
    MarkingModal,
    type MarkingModalProps,
} from '@/features/analytics/invite-links/ui/Marking'
import { useCreateMarkingFormData, UseSavedErid } from '@/shared/api/ord'
import { LoadingIndicator } from '@/shared/ui'
import { type MarkingStatus } from '@/shared/hooks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'

import classes from './MarkingCell.module.scss'
import { MarkingModalTrigger } from './MarkingModalTrigger'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import { wdPath, AdvertiserAccount } from '@/true-fsd/data/advertiser-account'

const { pickWwld } = workspaceWithLinkMethods

const MarkingCreatorButton = forwardRef<
    HTMLButtonElement,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'className'>
>(function MarkingCreatorButton({ ...props }, ref) {
    return (
        <button
            {...props}
            ref={ref}
            type="button"
            className={cl(classes.control, classes.text)}
        >
            Активировать маркировку
        </button>
    )
})

type MarkingCreatorProps = WorkspaceWithLinkDescriptor & {
    budget?: number
    onBudgetError?: () => void
}
const MarkingCreator = ({ budget, onBudgetError, ...props }: MarkingCreatorProps) => {
    const { mutate, isPending } = useCreateMarkingFormData(props)

    const onClick = () => {
        if (!budget) {
            onBudgetError?.()
            return
        }
        mutate({})
    }

    if (isPending) {
        return (
            <div className={cl(classes.control, classes.text)}>
                <LoadingIndicator
                    style={{
                        width: '20px',
                        height: '20px',
                        margin: 'auto',
                    }}
                />
            </div>
        )
    }

    return <MarkingCreatorButton onClick={onClick} />
}

type MarkingViewProps = {
    data: MarkingModalProps['analyticsTableRowType']
    onChangeAdminContactId: MarkingModalProps['handleSelectedAdminContactIdChange']
    markingModalFormDataQuery: UseQueryResult<ORDMarkingFormData, Error>
    savedEridKktu: UseSavedErid
    budget?: number
    onBudgetError?: () => void
} & Pick<
    MarkingModalProps,
    | 'groupId'
    | 'workspace_type'
    | 'workspace_id'
    | 'link_id'
    | 'ordAdditionalData'
>
const MarkingView = ({
    groupId,
    data,
    onChangeAdminContactId,
    markingModalFormDataQuery,
    ordAdditionalData,
    savedEridKktu,
    budget,
    onBudgetError,
    ...props
}: MarkingViewProps) => {
    const wwld = pickWwld(props)

    if (markingModalFormDataQuery.data) {
        return (
            <MarkingModal
                {...wwld}
                savedEridKktu={savedEridKktu}
                groupId={groupId}
                analyticsTableRowType={data}
                handleSelectedAdminContactIdChange={onChangeAdminContactId}
                ordAdditionalData={ordAdditionalData}
                markingFormData={markingModalFormDataQuery.data}
                trigger={
                    <MarkingModalTrigger
                        markingFormData={markingModalFormDataQuery.data}
                        analyticsTableRowType={data}
                    />
                }
            />
        )
    } else if (markingModalFormDataQuery.isPending) {
        return (
            <LoadingIndicator
                style={{
                    width: '20px',
                    height: '20px',
                    margin: 'auto',
                }}
            />
        )
    }

    return <MarkingCreator {...wwld} budget={budget} onBudgetError={onBudgetError} />
}

export type MarkingCellProps = Omit<MarkingViewProps, 'ordAdditionalData'> & {
    markingStatus: MarkingStatus
    groupId: number
    activeAccount: AdvertiserAccount | null
}

export const MarkingCell = ({
    markingStatus,
    activeAccount,
    ...props
}: MarkingCellProps) => {
    let tooltipTrigger: ReactNode
    let tooltipContent: ReactNode
    if (parseTelegramPeerReference(props.data.name)) {
        if (markingStatus.status === 'available') {
            return <MarkingView {...props} ordAdditionalData={markingStatus} />
        } else {
            tooltipTrigger = <MarkingCreatorButton disabled />
            const { vkOrdApiKey, adOrdContragentId, folderUrl, adOrdAgencyId } =
                markingStatus.missing
            tooltipContent = (
                <div className="text-left">
                    <p>
                        Чтобы активировать маркировку, заполните необходимые
                        поля <br />
                        на странице{' '}
                        <NavLink
                            to={wdPath(
                                activeAccount,
                                props,
                                'settings/marking'
                            )}
                            className="text-blue-primary"
                        >
                            Маркировка и документооборот
                        </NavLink>
                    </p>
                    <ul className="list-inside list-disc">
                        {vkOrdApiKey && <li>Интеграция с ОРД</li>}
                        {adOrdContragentId && <li>Даннные рекламодателя</li>}
                        {adOrdAgencyId && <li>Даннные агентства</li>}
                        {folderUrl && (
                            <li>Папка на Google Drive для документов</li>
                        )}
                    </ul>
                </div>
            )
        }
    } else {
        tooltipTrigger = (
            <div className={classes.empty}>
                <div className="long-dash grow">
                    <hr />
                </div>
            </div>
        )
        tooltipContent = <p>Маркировка недоступна</p>
    }

    return (
        <Tooltip delayDuration={500}>
            <TooltipTrigger asChild>{tooltipTrigger}</TooltipTrigger>
            <TooltipContent side="top">{tooltipContent}</TooltipContent>
        </Tooltip>
    )
}
